import CustomTooltip from '../../../components/common/tooltip';
import { toolTipInfo } from '../../../page-builder/utils/constants';
import { CustomField } from '@measured/puck';
import React, { useEffect, useRef } from 'react';

interface InputProps {
  value: string | undefined | number;
  placeholder?: string;
  onChange: (_value: string) => void;
  field?: CustomField<string>;
  label?: string;
  isPrefixText?: string;
  className?: string;
  onBlur?: (_event: React.FocusEvent<HTMLInputElement>) => void;
  tooltip?: string;
  disabled?: boolean;
}

const InputField = ({
  value,
  onChange,
  placeholder,
  field,
  label,
  className,
  isPrefixText,
  onBlur,
  disabled = false,
}: InputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Normalize pasted text
  const normalizeText = (text: string) => {
    return text.normalize('NFKC'); // Normalize to NFKC form
  };

  // Handle paste event
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault(); // Prevent default paste behavior

      // Get clipboard data
      const clipboardData = e.clipboardData;
      if (!clipboardData) {
        return;
      }

      const pastedText = clipboardData.getData('text/plain');

      // Normalize and clean the pasted text
      const cleanedText = normalizeText(pastedText);

      // Update the input value with the cleaned text
      if (inputRef.current) {
        const inputElement = inputRef.current;
        const startPosition = inputElement.selectionStart || 0;
        const endPosition = inputElement.selectionEnd || 0;

        // Set the cleaned content into the input field
        const newValue =
          value?.toString().substring(0, startPosition) +
          cleanedText +
          value?.toString().substring(endPosition);

        // Update the value
        onChange(newValue);

        // Move the cursor to the correct position after paste
        setTimeout(() => {
          inputElement.setSelectionRange(
            startPosition + cleanedText.length,
            startPosition + cleanedText.length,
          );
        }, 0);
      }
    };

    // Add the paste event listener to the input element
    if (inputRef.current) {
      inputRef.current.addEventListener('paste', handlePaste);
    }

    // Clean up the event listener when the component unmounts
    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('paste', handlePaste);
      }
    };
  }, [value]);
  return (
    <div>
      {field && (
        <CustomTooltip title={field?.label as keyof typeof toolTipInfo}>
          <label className="text-sm font-bold">{field?.label}</label>
        </CustomTooltip>
      )}
      {label && (
        <label htmlFor="email" className="block text-sm font-medium pb-3">
          {label}
        </label>
      )}
      <div className="relative w-full">
        {isPrefixText && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg">
            {isPrefixText}
          </span>
        )}
        <input
          ref={inputRef}
          type="text"
          disabled={disabled}
          className={`w-full ${isPrefixText ? 'pl-7 pr-4' : 'px-4'} py-3 rounded-md ${className}`}
          value={value}
          placeholder={placeholder}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onChange(event.target?.value)
          }
          onBlur={onBlur}
        />
      </div>
    </div>
  );
};

export default InputField;
